// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useEffect } from 'react'
import { AlertTriangle, Wrench } from 'lucide-react'
import { guides } from './HowToGuide'
import { supabase } from '../supabase'

const TASK_CAT_MAP = {
  hvac: ['HVAC', 'hvac'], air_handler: ['HVAC', 'hvac'], heat_pump: ['HVAC', 'hvac'],
  whole_house_fan: ['HVAC', 'hvac'], air_filtration: ['HVAC', 'hvac'], radiant_heat: ['HVAC', 'hvac'],
  water_heater: ['Plumbing', 'plumbing'], water_softener: ['Plumbing', 'plumbing'],
  garbage_disposal: ['Plumbing', 'plumbing'], septic: ['Plumbing', 'plumbing'],
  well: ['Plumbing', 'plumbing'], sump_pump: ['Plumbing', 'plumbing'],
  washer: ['Appliance Repair', 'appliance_repair'], dryer: ['Appliance Repair', 'appliance_repair'],
  dishwasher: ['Appliance Repair', 'appliance_repair'], refrigerator: ['Appliance Repair', 'appliance_repair'],
  range: ['Appliance Repair', 'appliance_repair'], oven: ['Appliance Repair', 'appliance_repair'],
  microwave: ['Appliance Repair', 'appliance_repair'],
  garage_door: ['Handyman', 'handyman', 'garage'],
  electrical_panel: ['Electrical', 'electrical'], smoke_detector: ['Electrical', 'electrical'],
  solar: ['Electrical', 'electrical'], generator: ['Electrical', 'electrical'],
  battery: ['Electrical', 'electrical'],
  pool: ['Pool Service', 'pool_service', 'pool'],
  roofing: ['Roofing', 'roofing'],
}

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  successBg: '#EBF5EF', dangerBg: '#FDECEA', warningBg: '#FBF3E8',
}
const f = 'Georgia, serif'

function calcNextDue(lastDate, interval) {
  if (!lastDate || !interval) return null
  const d = new Date(lastDate)
  const i = interval.toLowerCase()
  if (i.includes('every use') || i.includes('each use')) return null
  if (i.includes('month') && i.includes('3')) d.setDate(d.getDate() + 90)
  else if (i.includes('month') && i.includes('6')) d.setDate(d.getDate() + 180)
  else if (i.includes('month')) d.setDate(d.getDate() + 30)
  else if (i.includes('90')) d.setDate(d.getDate() + 90)
  else if (i.includes('2 year')) d.setDate(d.getDate() + 730)
  else if (i.includes('year') || i.includes('annual')) d.setDate(d.getDate() + 365)
  else if (i.includes('180')) d.setDate(d.getDate() + 180)
  else if (i.includes('week')) d.setDate(d.getDate() + 7)
  else return null
  return d.toISOString().split('T')[0]
}

function getDueStatus(nextDue) {
  if (!nextDue) return null
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const diffDays = Math.ceil((new Date(nextDue) - today) / (1000 * 60 * 60 * 24))
  if (diffDays < 0) return 'overdue'
  if (diffDays <= 30) return 'soon'
  return 'good'
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return dateStr }
}

const typeBadges = {
  diy:        [{ bg: '#F2EDE4', text: '#9A7B68', label: 'DIY' }],
  contractor: [{ bg: '#EBF5EF', text: '#2E6B4A', label: 'Contractor' }],
  auto_order: [
    { bg: '#F2EDE4', text: '#9A7B68', label: 'DIY' },
    { bg: '#FBF3E8', text: '#B8721F', label: 'Order Parts' },
  ],
}
const difficultyColors = {
  'Easy':     { bg: '#EBF5EF', text: '#2E6B4A' },
  'Moderate': { bg: '#FBF3E8', text: '#B8721F' },
  'Advanced': { bg: '#FDECEA', text: '#B03020' },
}

const inp = {
  width: '100%', background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: '8px', padding: '10px 14px', fontSize: '16px',
  fontFamily: f, color: C.brown, boxSizing: 'border-box', outline: 'none',
  marginBottom: '14px', WebkitUserSelect: 'text', userSelect: 'text',
  touchAction: 'manipulation', WebkitAppearance: 'none',
}
const lbl = {
  fontSize: '10px', color: C.muted, letterSpacing: '1.5px',
  textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif',
  display: 'block', marginBottom: '5px',
}
const ghostBtn = {
  background: 'transparent', border: `1px solid ${C.border}`,
  borderRadius: '12px', padding: '13px', fontSize: '13px',
  color: C.muted, cursor: 'pointer', fontFamily: f,
  letterSpacing: '1px', textTransform: 'uppercase',
}
const primaryBtn = {
  background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
  border: 'none', borderRadius: '12px', padding: '15px',
  fontSize: '14px', color: C.card, fontWeight: '700',
  cursor: 'pointer', fontFamily: f, letterSpacing: '1px', textTransform: 'uppercase',
}

// Props:
//   task           — { task, interval, type, partCost }
//   applianceName  — display string shown under task title
//   applianceType  — slug (e.g. 'hvac') used to look up the how-to guide
//   completedTasks — { [taskKey]: { date } }  keyed by `task_interval`
//   taskDates      — { [taskName | taskKey]: { lastCompleted, nextDue, interval } }
//   onComplete({ taskKey, date, cost, provider, note }) — parent handles save + close
//   onMarkIncomplete() — optional, undo completion
//   onDismiss      — close without saving
//   onFindContractor — optional, opens contractor directory
export default function MaintenanceTaskModal({
  task,
  applianceName,
  applianceType,
  completedTasks = {},
  taskDates = {},
  onComplete,
  onMarkIncomplete,
  onDismiss,
  onFindContractor,
  onOrderNow,
}) {
  const today = new Date().toISOString().split('T')[0]
  const [mode, setMode] = useState('detail')
  const [completionDate, setCompletionDate] = useState(today)
  const [suggestions, setSuggestions] = useState([])

  useEffect(() => {
    if (task.type !== 'contractor' || !applianceType) return
    const cats = TASK_CAT_MAP[applianceType] || ['General', 'general', 'Handyman', 'handyman']
    supabase.from('contractors')
      .select('id, name, business_name, rating, review_count, verified')
      .overlaps('category', cats)
      .eq('active', true)
      .order('rating', { ascending: false })
      .limit(3)
      .then(({ data }) => setSuggestions(data || []))
  }, [task.type, applianceType])
  const [costAmount, setCostAmount] = useState('')
  const [costProvider, setCostProvider] = useState('')
  const [costNote, setCostNote] = useState('')
  const [didItMyself, setDidItMyself] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const taskKey = `${task.task}_${task.interval}`
  const isChecked = !!completedTasks[taskKey]
  const td = taskDates[task.task] || taskDates[taskKey]
  const nextDue = td?.nextDue
  const lastCompleted = td?.lastCompleted || completedTasks[taskKey]?.date
  const hasRecord = isChecked || !!lastCompleted
  const status = getDueStatus(nextDue)

  const guideKey = `${applianceType}_${task.task.toLowerCase().replace(/\s+/g, '_')}`
  const guide = guides[guideKey]
  const badges = typeBadges[task.type] || typeBadges.diy
  const dc = guide?.difficulty ? difficultyColors[guide.difficulty] : null

  const nextDuePreview = completionDate && task.interval ? calcNextDue(completionDate, task.interval) : null

  const handleSave = async () => {
    setSubmitting(true)
    const date = completionDate || today
    const cost = costAmount ? parseFloat(costAmount) : null
    await onComplete({ taskKey, date, cost, provider: didItMyself ? null : (costProvider || null), note: costNote || null, didItMyself })
    setSubmitting(false)
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && onDismiss()}
    >
      <div style={{ width: '100%', maxWidth: '680px', maxHeight: '90vh', background: C.card, borderRadius: '20px 20px 0 0', border: `1px solid ${C.border}`, borderBottom: 'none', boxShadow: '0 -8px 40px rgba(44,29,20,0.2)', display: 'flex', flexDirection: 'column', fontFamily: f }}>

        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '12px', paddingBottom: '6px', flexShrink: 0 }}>
          <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: C.border }} />
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', flex: 1 }}>

          {mode === 'detail' ? (
            <>
              {/* Header */}
              <div style={{ padding: '4px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, paddingRight: '12px' }}>
                  <div style={{ fontSize: '21px', fontWeight: '700', color: C.brown, lineHeight: 1.2 }}>{task.task}</div>
                  <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '4px' }}>{applianceName}</div>
                </div>
                <button onClick={onDismiss} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: C.muted, padding: '0', lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>

              <div style={{ padding: '14px 24px 32px' }}>

                {/* Meta badges: type(s), interval, difficulty */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  {badges.map((b, i) => {
                    const isClickable = b.label === 'Order Parts' && !!onOrderNow
                    const baseStyle = { fontSize: '11px', padding: '4px 12px', borderRadius: '20px', background: b.bg, color: b.text, border: `1px solid ${b.text}33`, fontFamily: 'Calibri, sans-serif', fontWeight: '600' }
                    if (isClickable) {
                      return (
                        <button
                          key={i}
                          onClick={(e) => { e.stopPropagation(); onOrderNow() }}
                          title="Order parts on Amazon"
                          style={{ ...baseStyle, cursor: 'pointer' }}
                        >
                          {b.label} →
                        </button>
                      )
                    }
                    return <span key={i} style={baseStyle}>{b.label}</span>
                  })}
                  <span style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '20px', background: C.bg, color: C.muted, border: `1px solid ${C.border}`, fontFamily: 'Calibri, sans-serif' }}>{task.interval}</span>
                  {dc && <span style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '20px', background: dc.bg, color: dc.text, border: `1px solid ${dc.text}33`, fontFamily: 'Calibri, sans-serif', fontWeight: '600' }}>{guide.difficulty}</span>}
                </div>

                {/* History + due date — always visible */}
                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '3px' }}>Last Completed</div>
                      <div style={{ fontSize: '13px', fontFamily: 'Calibri, sans-serif', color: lastCompleted ? C.success : C.muted, fontStyle: lastCompleted ? 'normal' : 'italic', fontWeight: lastCompleted ? '600' : '400' }}>
                        {lastCompleted ? formatDate(lastCompleted) : 'No history yet'}
                      </div>
                    </div>
                    {nextDue && (
                      <div>
                        <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '3px' }}>Next Due</div>
                        <div style={{ fontSize: '13px', fontFamily: 'Calibri, sans-serif', fontWeight: status === 'overdue' || status === 'soon' ? '700' : '600', color: status === 'overdue' ? C.danger : status === 'soon' ? C.amber : C.success }}>
                          {formatDate(nextDue)}{status === 'overdue' ? ' — OVERDUE' : status === 'soon' ? ' — due soon' : ''}
                        </div>
                      </div>
                    )}
                  </div>
                  {onMarkIncomplete && hasRecord && (
                    <div style={{ marginTop: '10px', borderTop: `1px solid ${C.border}`, paddingTop: '10px' }}>
                      <button
                        onClick={onMarkIncomplete}
                        style={{ background: 'transparent', border: `1px solid ${C.muted}44`, borderRadius: '8px', padding: '5px 10px', fontSize: '10px', color: C.muted, cursor: 'pointer', fontFamily: 'Calibri, sans-serif' }}
                      >Mark Incomplete</button>
                    </div>
                  )}
                </div>

                {/* Part cost estimate */}
                {task.partCost && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', background: C.warningBg, border: `1px solid ${C.amber}33`, borderRadius: '8px', padding: '6px 12px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', fontWeight: '600' }}>~{task.partCost} in parts</span>
                  </div>
                )}

                {/* How-to guide content */}
                {guide ? (
                  <>
                    {guide.time && (
                      <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '14px' }}>
                        Estimated time: <strong style={{ color: C.brown }}>{guide.time}</strong>
                      </div>
                    )}

                    {guide.warning && (
                      <div style={{ background: C.dangerBg, border: `1px solid ${C.danger}44`, borderRadius: '10px', padding: '12px 14px', marginBottom: '14px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <AlertTriangle size={14} strokeWidth={1.5} color={C.danger} style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div style={{ fontSize: '13px', color: C.danger, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>
                          <strong>Safety note:</strong> {guide.warning}
                        </div>
                      </div>
                    )}

                    {guide.contractorNote && (
                      <div style={{ background: C.successBg, border: `1px solid ${C.success}44`, borderRadius: '10px', padding: '12px 14px', marginBottom: '14px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <Wrench size={14} strokeWidth={1.5} color={C.success} style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div style={{ fontSize: '13px', color: C.success, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>
                          <strong>Contractor note:</strong> {guide.contractorNote}
                        </div>
                      </div>
                    )}

                    {guide.tools?.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', fontWeight: '700', marginBottom: '8px' }}>What You Need</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {guide.tools.map((tool, i) => (
                            <span key={i} style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '20px', background: C.bg, border: `1px solid ${C.border}`, color: C.brown, fontFamily: 'Calibri, sans-serif' }}>{tool}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {guide.steps?.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', fontWeight: '700', marginBottom: '12px' }}>Steps</div>
                        {guide.steps.map((step, i) => (
                          <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '14px', alignItems: 'flex-start' }}>
                            <div style={{ width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, color: C.card, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', fontFamily: 'Calibri, sans-serif' }}>{i + 1}</div>
                            <div style={{ fontSize: '14px', color: C.brown, lineHeight: 1.7, fontFamily: 'Calibri, sans-serif', paddingTop: '3px' }}>{step}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {guide.proTip && (
                      <div style={{ background: C.warningBg, border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '14px 16px', marginBottom: '8px' }}>
                        <div style={{ fontSize: '10px', color: C.amber, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', fontWeight: '700', marginBottom: '4px' }}>Pro Tip</div>
                        <div style={{ fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>{guide.proTip}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', fontStyle: 'italic', marginBottom: '8px' }}>
                    No step-by-step guide available for this task yet.
                  </div>
                )}

                {/* Inline contractor suggestions */}
                {task.type === 'contractor' && suggestions.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', fontWeight: '700', marginBottom: '10px' }}>Suggested Contractors</div>
                    {suggestions.map(c => (
                      <div key={c.id} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '11px 14px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: C.brown, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.business_name || c.name}</div>
                          {c.rating > 0 && (
                            <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>
                              ★ {Number(c.rating).toFixed(1)} · {c.review_count} review{c.review_count !== 1 ? 's' : ''}
                              {c.verified && <span style={{ marginLeft: '6px', color: C.success, fontWeight: '600' }}>✓</span>}
                            </div>
                          )}
                        </div>
                        {onFindContractor && (
                          <button
                            onClick={onFindContractor}
                            style={{ background: 'transparent', border: `1px solid ${C.amber}`, borderRadius: '8px', padding: '5px 12px', fontSize: '12px', color: C.amber, cursor: 'pointer', fontFamily: f, fontWeight: '600', flexShrink: 0, marginLeft: '10px' }}
                          >View →</button>
                        )}
                      </div>
                    ))}
                    {onFindContractor && (
                      <div style={{ fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', cursor: 'pointer', textAlign: 'right', marginTop: '2px' }} onClick={onFindContractor}>
                        View all contractors →
                      </div>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {task.type === 'auto_order' && onOrderNow && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onOrderNow() }}
                      style={{ background: '#FBF3E8', border: `1px solid ${C.amber}44`, borderRadius: '12px', padding: '13px', fontSize: '13px', color: C.amber, cursor: 'pointer', fontFamily: f, fontWeight: '700' }}
                    >
                      🛒 Order Parts on Amazon
                    </button>
                  )}
                  {task.type === 'contractor' && onFindContractor && (
                    <button
                      onClick={onFindContractor}
                      style={{ background: C.successBg, border: `1px solid ${C.success}44`, borderRadius: '12px', padding: '13px', fontSize: '13px', color: C.success, cursor: 'pointer', fontFamily: f, fontWeight: '700' }}
                    >
                      🔨 Find a Contractor
                    </button>
                  )}
                  {!isChecked && onComplete && (
                    <button onClick={() => setMode('complete')} style={primaryBtn}>
                      Mark Complete
                    </button>
                  )}
                  {isChecked && onComplete && (
                    <button onClick={() => setMode('complete')} style={{ ...ghostBtn, borderColor: `${C.amber}66`, color: C.amber }}>
                      Log Again
                    </button>
                  )}
                  <button onClick={onDismiss} style={ghostBtn}>Cancel</button>
                </div>
              </div>
            </>
          ) : (
            /* ── Mark Complete sub-form ── */
            <div style={{ padding: '4px 24px 32px' }}>
              <div style={{ fontSize: '19px', fontWeight: '700', color: C.brown, marginBottom: '4px' }}>Mark Complete</div>
              <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '24px' }}>{task.task} · {applianceName}</div>

              <label style={lbl}>Date Completed</label>
              <input
                type="date"
                value={completionDate}
                max={today}
                onChange={e => setCompletionDate(e.target.value)}
                style={inp}
              />
              {nextDuePreview && (
                <div style={{ fontSize: '11px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginTop: '-10px', marginBottom: '14px' }}>
                  Next due: {formatDate(nextDuePreview)}
                </div>
              )}

              <label style={lbl}>Cost (optional)</label>
              <input
                type="number"
                placeholder="e.g. 45"
                value={costAmount}
                onChange={e => setCostAmount(e.target.value)}
                style={inp}
              />

              <div
                style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: didItMyself ? '14px' : '6px', cursor: 'pointer' }}
                onClick={() => { setDidItMyself(v => !v); if (!didItMyself) setCostProvider('') }}
              >
                <div style={{ width: '20px', height: '20px', borderRadius: '5px', border: `2px solid ${didItMyself ? C.amber : C.border}`, background: didItMyself ? C.amber : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                  {didItMyself && <span style={{ color: '#fff', fontSize: '13px', fontWeight: '700', lineHeight: 1 }}>✓</span>}
                </div>
                <span style={{ fontSize: '13px', color: didItMyself ? C.amber : C.brown, fontFamily: 'Calibri, sans-serif', fontWeight: didItMyself ? '700' : '400' }}>I did it myself (DIY)</span>
              </div>

              {!didItMyself && (
                <>
                  <label style={lbl}>Service Provider (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. HVAC Pro Services"
                    value={costProvider}
                    onChange={e => setCostProvider(e.target.value)}
                    style={inp}
                  />
                </>
              )}

              <label style={lbl}>Note (optional)</label>
              <input
                type="text"
                placeholder="e.g. Used Nordic Pure filters this time"
                value={costNote}
                onChange={e => setCostNote(e.target.value)}
                style={{ ...inp, marginBottom: '24px' }}
              />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  onClick={handleSave}
                  disabled={submitting}
                  style={{ ...primaryBtn, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}
                >{submitting ? 'Saving...' : '✓ Save & Complete Task'}</button>
                <button onClick={() => setMode('detail')} style={ghostBtn}>Back</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
